import { createMRTColumnHelper, MRT_ColumnDef } from 'material-react-table'
import { useMemo } from 'react'

import { CodeCell, ExpandableTextCell, getMrtTableDisabled, MonacoScriptCell, TextCell } from '@/components'
import { createHeaderWithTooltip, createRequiredHeaderWithTooltip } from '@/components/MrtTable/helpers'
import { ItemPropertyType } from '@/enums'
import { useAppContext } from '@/hooks'
import { UpdateCellValueHandler } from '@/hooks/useTableHandlers'
import { useDetailTabContext } from '@/pages/User/pages/Home/components/DetailTab'
import { ViewColumn } from '@/rtkApi/mddApiSliceGeneratedV2'

type UseViewColumnsV2Props = {
	generateColumnCode: boolean
	isUpperCase: boolean
	onNameCommit?: (nameValue: string, rowIndex: number) => void
	updateCellValue: UpdateCellValueHandler<ViewColumn>
}

export const useViewColumnsV2 = ({
	generateColumnCode,
	updateCellValue,
	isUpperCase,
	onNameCommit,
}: UseViewColumnsV2Props) => {
	const { t } = useAppContext()
	const {
		state: { editMode },
	} = useDetailTabContext()

	const columnHelper = createMRTColumnHelper<ViewColumn>()

	const columns = useMemo(() => {
		return [
			columnHelper.accessor('name', {
				...createRequiredHeaderWithTooltip(t('VIEW_COLUMNS_NAME')),
				size: 180,
				minSize: 120,
				meta: {
					type: ItemPropertyType.TEXT,
					isUpperCase,
				},
				Edit: ({ cell, table, row, column }) => {
					const isDisabled = getMrtTableDisabled<ViewColumn>({
						editMode,
						row,
						column,
					})

					return (
						<TextCell<ViewColumn>
							cell={cell}
							table={table}
							row={row}
							column={column}
							updateCellValue={updateCellValue}
							isDisabled={isDisabled}
							onCommit={
								editMode && generateColumnCode ? onNameCommit : undefined
							}
						/>
					)
				},
			}),
			columnHelper.accessor('code', {
				...createRequiredHeaderWithTooltip(t('VIEW_COLUMNS_CODE')),
				size: 180,
				minSize: 120,
				meta: {
					type: ItemPropertyType.TEXT,
					isCodeGenerated: generateColumnCode,
				},
				Edit: ({ cell, table, row, column }) => {
					const isDisabled = getMrtTableDisabled<ViewColumn>({
						editMode,
						row,
						column,
					})

					return (
						<CodeCell
							cell={cell}
							column={column}
							table={table}
							row={row}
							isDisabled={isDisabled || generateColumnCode}
							updateCellValue={updateCellValue}
							showGenerateButton={editMode && !generateColumnCode}
							isUpperCase={isUpperCase}
						/>
					)
				},
			}),
		columnHelper.accessor('description', {
			...createHeaderWithTooltip(t('VIEW_COLUMNS_DESCRIPTION')),
			size: 200,
			minSize: 150,
			meta: {
				type: ItemPropertyType.TEXTAREA,
			},
			Edit: ({ cell, row, column }) => {
				const isDisabled = getMrtTableDisabled<ViewColumn>({
					editMode,
					row,
					column,
				})

				return (
					<ExpandableTextCell
						cell={cell}
						column={column}
						row={row}
						updateCellValue={updateCellValue}
						isDisabled={isDisabled}
						isReadOnly={!editMode}
					/>
				)
			},
		}),
		columnHelper.accessor('comment', {
			...createHeaderWithTooltip(t('VIEW_COLUMNS_COMMENT')),
			size: 200,
			minSize: 150,
			meta: {
				type: ItemPropertyType.TEXTAREA,
			},
			Edit: ({ cell, row, column }) => {
				const isDisabled = getMrtTableDisabled<ViewColumn>({
					editMode,
					row,
					column,
				})

				return (
					<ExpandableTextCell
						cell={cell}
						column={column}
						row={row}
						updateCellValue={updateCellValue}
						isDisabled={isDisabled}
						isReadOnly={!editMode}
					/>
				)
			},
		}),
			columnHelper.accessor('mapping', {
				...createHeaderWithTooltip(t('VIEW_COLUMNS_MAPPING')),
				size: 250,
				minSize: 180,
				meta: {
					type: ItemPropertyType.ACE_EDITOR,
				},
				Edit: ({ cell, table: _table, row, column }) => {
					const isDisabled = getMrtTableDisabled<ViewColumn>({
						editMode,
						row,
						column,
					})

					return (
						<MonacoScriptCell
							cell={cell}
							column={column}
							row={row}
							updateCellValue={updateCellValue}
							isDisabled={isDisabled}
							isReadOnly={!editMode}
							language="sql"
						/>
					)
				},
			}),
		]
	}, [
		columnHelper,
		editMode,
		generateColumnCode,
		isUpperCase,
		onNameCommit,
		t,
		updateCellValue,
	])

	return columns as MRT_ColumnDef<ViewColumn>[]
}
